/**
 * 북마크 화면 전용 API.
 *
 * 기존에는 컴포넌트마다 raw fetch + 수동 Authorization 헤더를 써서
 * 토큰 갱신·에러 정규화·도메인별 baseURL 처리를 모두 우회했다.
 * 공용 axios 클라이언트(apiService.client)를 통하도록 한 곳에 모은다.
 */

import { apiService } from './api'
import type {
  BookmarkListResponse,
  BookmarkQuery,
  BookmarkUrl,
  BulkActionRequest,
  BulkActionResult,
} from '../../types/bookmark'

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  message?: string
}

/** 백엔드 응답(id)을 화면 모델(_id)로 정규화 */
function toBookmarkUrl(raw: Record<string, any>): BookmarkUrl {
  return {
    _id: raw.id ?? raw._id,
    originalUrl: raw.originalUrl,
    shortUrl: raw.shortUrl,
    shortCode: raw.shortCode,
    customCode: raw.customCode,
    title: raw.title,
    description: raw.description,
    favicon: raw.favicon,
    clickCount: raw.clickCount ?? 0,
    lastClickedAt: raw.lastClickedAt,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    tags: raw.tags ?? [],
    isFavorite: raw.isFavorite ?? false,
    notes: raw.notes,
    folderId: raw.folderId,
    metadata: raw.metadata,
    expiresAt: raw.expiresAt,
    hasPassword: raw.hasPassword ?? raw.security?.hasPassword ?? false,
    isExpiring: raw.isExpiring ?? raw.security?.isExpiring ?? false,
    isExpired: raw.isExpired ?? raw.security?.isExpired ?? false,
    security: raw.security,
  }
}

function buildParams(query: BookmarkQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {
    page: query.page ?? 1,
    limit: query.limit ?? 20,
    sortBy: query.sortBy ?? 'updatedAt',
    order: query.sortOrder ?? 'desc',
  }
  if (query.folderId) {
    params.folderId = query.folderId
  }
  if (query.tags?.length) {
    params.tags = query.tags
  }
  if (query.search) {
    params.search = query.search
  }
  if (query.favorite) {
    params.favorite = 'true'
  }
  if (query.security) {
    params.security = query.security
  }
  if (query.preset) {
    params.preset = query.preset
  }
  return params
}

export const bookmarkApi = {
  /** 북마크 목록 (폴더·태그·검색·보안 필터, 페이징, 정렬) */
  async list(query: BookmarkQuery): Promise<BookmarkListResponse> {
    const response = await apiService.client.get<ApiEnvelope<any>>('/urls', {
      params: buildParams(query),
    })
    const data = response.data.data

    return {
      urls: (data?.urls ?? []).map(toBookmarkUrl),
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      limit: data?.limit ?? query.limit ?? 20,
      totalPages: data?.totalPages ?? 1,
      hasNextPage: data?.hasNextPage ?? false,
      hasPrevPage: data?.hasPrevPage ?? false,
    }
  },

  /** 링크 생성. 서버 응답 봉투를 그대로 돌려준다. */
  async create(
    input: Record<string, unknown>
  ): Promise<{ success: boolean; data?: BookmarkUrl; message?: string }> {
    const response = await apiService.client.post<ApiEnvelope<BookmarkUrl>>(
      '/urls',
      input
    )
    return response.data
  },

  /** 링크 수정 */
  async update(
    urlId: string,
    input: Record<string, unknown>
  ): Promise<{
    success: boolean
    data?: { url?: BookmarkUrl }
    message?: string
  }> {
    const response = await apiService.client.put<
      ApiEnvelope<{ url?: BookmarkUrl }>
    >(`/urls/${urlId}`, input)
    return response.data
  },

  /** 단일 링크 삭제 */
  async remove(urlId: string): Promise<void> {
    await apiService.client.delete<ApiEnvelope<void>>(`/urls/${urlId}`)
  },

  /** 폴더 이동 */
  async moveToFolder(urlId: string, folderId: string | null): Promise<void> {
    await apiService.client.patch<ApiEnvelope<void>>(`/urls/${urlId}/folder`, {
      folderId,
    })
  },

  /** 즐겨찾기 토글 */
  async toggleFavorite(urlId: string): Promise<boolean> {
    const response = await apiService.client.patch<
      ApiEnvelope<{ isFavorite: boolean }>
    >(`/urls/${urlId}/favorite`)
    return response.data.data?.isFavorite ?? false
  },

  /** 일괄 작업 (이동 / 즐겨찾기 / 삭제) */
  async bulk(request: BulkActionRequest): Promise<BulkActionResult> {
    const response = await apiService.client.post<
      ApiEnvelope<BulkActionResult>
    >('/urls/bulk', request)
    return {
      action: request.action,
      requested: response.data.data?.requested ?? request.urlIds.length,
      affected: response.data.data?.affected ?? 0,
      message: response.data.message ?? '',
    }
  },

  /** 태그 삭제 (모든 링크에서 제거됨) */
  async deleteTag(tagId: string): Promise<void> {
    await apiService.client.delete<ApiEnvelope<void>>(`/tags/${tagId}`)
  },
}

export default bookmarkApi
